import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AgentEvents,
  ThreadPinnedEvent,
  ThreadUnpinnedEvent,
  ThreadArchivedEvent,
  ThreadRenamedEvent,
} from '@dreamai/types';
import api from '../../api';
import { usePusher } from '../../contexts/PusherContext';
import SubThreadToggle from './sidebar/SubThreadToggle';
import { ReactComponent as TrashIcon } from '../../icons/trash-rounded.svg';
import { ReactComponent as CheckIcon } from '../../icons/check.svg';
import { ReactComponent as SidebarPanelIcon } from '../../icons/sidebar-panel.svg';
import { ReactComponent as CanvasToggleIcon } from '../../icons/canvas-toggle.svg';
import ConfirmModal from '../confirm-modals/ConfirmModal';
import './chat-header.scss';

interface HumanTaskInfo {
  taskId: string;
}

interface ChatHeaderProps {
  threadId: string | null;
  isMobile?: boolean;
  onToggleSidebar?: () => void;
  showCanvasToggle?: boolean;
  onToggleCanvas?: () => void;
}

const ChatHeader: FC<ChatHeaderProps> = ({
  threadId,
  isMobile,
  onToggleSidebar,
  showCanvasToggle,
  onToggleCanvas,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [searchParams] = useSearchParams();
  const isNewConversation = searchParams.get('new') === 'true';
  const pusherContext = usePusher(true);
  const userChannel = pusherContext?.userChannel;
  const companyChannel = pusherContext?.pusherChannel;

  // Thread info state
  const [threadDescription, setThreadDescription] = useState<
    string | undefined
  >();
  const [isMainThread, setIsMainThread] = useState(false);
  const [isInternalMonologue, setIsInternalMonologue] = useState(false);
  const [isWhatsappThread, setIsWhatsappThread] = useState(false);
  const [isViaCompany, setIsViaCompany] = useState(false);
  const [sharePermission, setSharePermission] = useState<string | undefined>();
  const [companyPermission, setCompanyPermission] = useState<
    string | undefined
  >();
  const [hasShares, setHasShares] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [isCampaignThread, setIsCampaignThread] = useState(false);
  const [campaignId, setCampaignId] = useState<number | null>(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isArchivedThread, setIsArchivedThread] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);
  const editInputRef = useRef<HTMLInputElement>(null);

  // Human task state (just to know this is a HT thread)
  const [humanTaskInfo, setHumanTaskInfo] = useState<HumanTaskInfo | null>(
    null,
  );

  // Fetch thread info
  const fetchThreadInfo = useCallback(async () => {
    if (!threadId) {
      setThreadDescription(undefined);
      setIsMainThread(false);
      setIsInternalMonologue(false);
      setIsWhatsappThread(false);
      setIsViaCompany(false);
      setSharePermission(undefined);
      setCompanyPermission(undefined);
      setHasShares(false);
      setIsPinned(false);
      setIsOwner(false);
      setIsCampaignThread(false);
      setCampaignId(null);
      setIsArchivedThread(false);
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      const response = await api.get(`/agents/threads/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success && response.data.thread) {
        const thread = response.data.thread;
        setThreadDescription(thread.description);
        setIsMainThread(thread.is_main_thread || false);
        setIsInternalMonologue(thread.is_internal_monologue || false);
        setIsWhatsappThread(thread.is_whatsapp_thread || false);
        setIsViaCompany(response.data.via_company || false);
        setSharePermission(response.data.share_permission);
        setCompanyPermission(response.data.company_permission);
        setHasShares(response.data.has_shares || response.data.via_company);
        setIsOwner(response.data.share_permission === 'OWNER');
        setIsArchivedThread(thread.is_archived_thread || false);
        // Check if this is a campaign thread and get the campaignId
        const campaignDiscussingObject = thread.discussing_objects?.find(
          (obj: { discussing_object: string }) =>
            obj.discussing_object === 'Campaign',
        );
        setIsCampaignThread(!!campaignDiscussingObject);
        setCampaignId(
          campaignDiscussingObject?.discussing_object_match?.campaignId ?? null,
        );

        // Check for HumanTask discussing object
        const humanTaskDiscObj = thread.discussing_objects?.find(
          (obj: { discussing_object: string }) =>
            obj.discussing_object === 'HumanTask',
        );
        if (humanTaskDiscObj?.discussing_object_match?.taskId) {
          setHumanTaskInfo({
            taskId: humanTaskDiscObj.discussing_object_match.taskId,
          });
        } else {
          setHumanTaskInfo(null);
        }
      }
    } catch (error) {
      console.error('Error fetching thread info:', error);
    }
  }, [threadId, getAccessTokenSilently]);

  // Refresh shares status by re-fetching thread info
  const refreshThreadShares = useCallback(() => {
    fetchThreadInfo();
  }, [fetchThreadInfo]);

  // Fetch pinned status
  const fetchPinnedStatus = useCallback(async () => {
    if (!threadId) return;

    try {
      const token = await getAccessTokenSilently();
      const response = await api.get('/agents/threads/pinned', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setIsPinned(response.data.pinnedThreadIds.includes(threadId));
      }
    } catch (error) {
      console.error('Error fetching pinned status:', error);
    }
  }, [threadId, getAccessTokenSilently]);

  // Toggle pin status (optimistic update)
  const handleTogglePin = useCallback(async () => {
    if (!threadId) return;

    const previousState = isPinned;
    // Optimistically update UI immediately
    setIsPinned(!isPinned);

    try {
      const token = await getAccessTokenSilently();
      const endpoint = previousState
        ? '/agents/threads/unpin'
        : '/agents/threads/pin';

      await api.post(
        endpoint,
        { threadId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (error) {
      console.error('Error toggling pin:', error);
      // Revert on error
      setIsPinned(previousState);
    }
  }, [threadId, isPinned, getAccessTokenSilently]);

  useEffect(() => {
    fetchThreadInfo();
    fetchPinnedStatus();
  }, [fetchThreadInfo, fetchPinnedStatus]);

  // Listen for pin/unpin events for real-time sync across devices
  useEffect(() => {
    if (!userChannel || !threadId) return;

    const handleThreadPinned = (data: ThreadPinnedEvent) => {
      if (data.threadId === threadId) {
        setIsPinned(true);
      }
    };

    const handleThreadUnpinned = (data: ThreadUnpinnedEvent) => {
      if (data.threadId === threadId) {
        setIsPinned(false);
      }
    };

    userChannel.bind(AgentEvents.THREAD_PINNED, handleThreadPinned);
    userChannel.bind(AgentEvents.THREAD_UNPINNED, handleThreadUnpinned);

    return () => {
      userChannel.unbind(AgentEvents.THREAD_PINNED, handleThreadPinned);
      userChannel.unbind(AgentEvents.THREAD_UNPINNED, handleThreadUnpinned);
    };
  }, [userChannel, threadId]);

  // Listen for thread rename events on company channel
  useEffect(() => {
    if (!companyChannel || !threadId) return;

    const handleThreadRenamed = (data: ThreadRenamedEvent) => {
      if (data.threadId === threadId) {
        setThreadDescription(data.newName);
      }
    };

    companyChannel.bind(AgentEvents.THREAD_RENAMED, handleThreadRenamed);

    return () => {
      companyChannel.unbind(AgentEvents.THREAD_RENAMED, handleThreadRenamed);
    };
  }, [companyChannel, threadId]);

  // Handle archive thread
  const handleArchive = useCallback(async () => {
    if (!threadId) return;

    setIsArchiving(true);
    try {
      const token = await getAccessTokenSilently();

      // Use campaign delete endpoint for campaign threads
      if (isCampaignThread && campaignId) {
        await api.delete(`/campaigns/${campaignId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await api.post(
          '/agents/threads/archive',
          { threadId },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }

      // Reload to show archived state
      window.location.reload();
    } catch (error: any) {
      console.error('Error archiving thread:', error);
      alert(error.response?.data?.error || 'Failed to delete');
    } finally {
      setIsArchiving(false);
      setShowArchiveConfirm(false);
    }
  }, [threadId, isCampaignThread, campaignId, getAccessTokenSilently]);

  // Determine if archive button should be shown
  // Show for owners of non-main, non-internal-monologue, non-archived, non-human-task threads
  const canArchive =
    isOwner &&
    !isMainThread &&
    !isInternalMonologue &&
    !isArchivedThread &&
    !humanTaskInfo;

  // Determine if share button should be disabled
  // Note: isViaCompany is NOT disabled - users should be able to see the company-wide access info
  const shareDisabled =
    isMainThread ||
    isInternalMonologue ||
    isWhatsappThread ||
    isArchivedThread ||
    sharePermission === 'READ' ||
    sharePermission === 'WRITE';

  // Get display title
  const getTitle = () => {
    if (isNewConversation) {
      return 'New Chat';
    }
    if (
      threadDescription &&
      !threadDescription.toLowerCase().startsWith('conversation')
    ) {
      return threadDescription;
    }
    return 'Chat';
  };

  // Can rename if not a campaign thread, not main thread, not archived
  const canRename =
    !isCampaignThread && !isMainThread && !isArchivedThread && threadId;

  // Handle double-click to start editing
  const handleTitleDoubleClick = () => {
    if (!canRename) return;
    setEditName(threadDescription || '');
    setIsEditing(true);
    setTimeout(() => editInputRef.current?.focus(), 0);
  };

  // Handle save rename
  const handleSaveRename = async () => {
    if (!threadId || !editName.trim() || isSavingName) return;

    setIsSavingName(true);
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        `/agents/threads/${threadId}/rename`,
        { newName: editName.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Pusher will update the name via THREAD_RENAMED event
      setIsEditing(false);
    } catch (error) {
      console.error('Error renaming thread:', error);
    } finally {
      setIsSavingName(false);
    }
  };

  // Handle key press in edit input
  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveRename();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
    }
  };

  return (
    <></>
    // <div className='chat-header'>
    //   {/* Mobile menu button */}
    //   {isMobile && onToggleSidebar && (
    //     <button
    //       className='mobile-menu-btn'
    //       onClick={onToggleSidebar}
    //       aria-label='Open sidebar'
    //     >
    //       <SidebarPanelIcon />
    //     </button>
    //   )}
    //   {isEditing ? (
    //     <div className='chat-title-edit'>
    //       <input
    //         ref={editInputRef}
    //         type='text'
    //         value={editName}
    //         onChange={(e) => setEditName(e.target.value)}
    //         onKeyDown={handleEditKeyDown}
    //         onBlur={() => setIsEditing(false)}
    //         maxLength={200}
    //       />
    //       <button
    //         className='save-btn'
    //         onMouseDown={(e) => e.preventDefault()}
    //         onClick={handleSaveRename}
    //         disabled={isSavingName || !editName.trim()}
    //       >
    //         <CheckIcon />
    //       </button>
    //     </div>
    //   ) : (
    //     <h1
    //       className={`chat-title ${canRename ? 'editable' : ''}`}
    //       onDoubleClick={handleTitleDoubleClick}
    //       title={canRename ? 'Double-click to rename' : undefined}
    //     >
    //       {getTitle()}
    //     </h1>
    //   )}

    //   {threadId && !isNewConversation && (
    //     <div className='actions'>
    //       {/* Archive button - only for owners of non-main, non-campaign, non-human-task threads */}
    //       {canArchive && (
    //         <button
    //           className='archive-btn'
    //           onClick={() => setShowArchiveConfirm(true)}
    //           title='Delete thread'
    //         >
    //           <TrashIcon />
    //         </button>
    //       )}

    //       {/* Archive confirmation modal */}
    //       <ConfirmModal
    //         show={showArchiveConfirm}
    //         handleClose={() => setShowArchiveConfirm(false)}
    //         handleConfirm={handleArchive}
    //         title={isCampaignThread ? 'Delete Campaign' : 'Delete Thread'}
    //         bodyText={
    //           isCampaignThread
    //             ? "You're about to delete this campaign and its thread. This action cannot be undone."
    //             : "You're about to delete this thread. This action cannot be undone."
    //         }
    //         confirmLabel={isArchiving ? 'Deleting...' : 'Delete'}
    //         cancelLabel='Cancel'
    //         confirmDisabled={isArchiving}
    //         singleButton={false}
    //         verticalButtons={false}
    //         littleSweetieVersion={false}
    //         confirmFirst={false}
    //       />
    //     </div>
    //   )}

    //   {/* Mobile canvas toggle button */}
    //   {isMobile && showCanvasToggle && onToggleCanvas && (
    //     <button
    //       className='canvas-toggle-btn'
    //       onClick={onToggleCanvas}
    //       aria-label='Open canvas'
    //     >
    //       <CanvasToggleIcon />
    //     </button>
    //   )}
    // </div>
  );
};

export default ChatHeader;
